import React from 'react';

/* ============================================================================
   Bespoke PhilHealth / Reimbursement icon set.
   Same two-tone language as MedicalIcons: primary stroke inherits
   `currentColor`, with a single PhilHealth-gold (#FFC72C) accent detail.
   On gold surfaces pass a contrasting `accent` (e.g. "#04291b").
   ========================================================================== */

const GOLD = '#FFC72C';
const BONE = 'M4 5.2a2.2 2.2 0 0 1 3.7 1.4v1c0 .5-.2.9-.5 1.3L5.7 11v2l1.5 2.1c.3.4.5.8.5 1.3v1a2.2 2.2 0 0 1-3.7 1.4 2.2 2.2 0 0 1-.6-2v-.9c0-.5.2-.9.5-1.3L5.4 13v-2L3.9 8.9c-.3-.4-.5-.8-.5-1.3v-1a2.2 2.2 0 0 1 .6-1.4z';

const svgBase = {
  xmlns: 'http://www.w3.org/2000/svg',
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
};

const Icon = ({ children, className, ...props }) => (
  <svg {...svgBase} className={className} {...props}>{children}</svg>
);

/** Shield + peso — covered & protected reimbursement */
export const ReimbursementShieldIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d="M12 2.5l7.5 3v5.2c0 4.8-3.2 8.9-7.5 10.1C7.7 19.6 4.5 15.5 4.5 10.7V5.5L12 2.5z" />
    <path d="M10.2 8.3v7.4" stroke={accent} />
    <path d="M10.2 8.3h2.3a2 2 0 0 1 0 4h-2.3" stroke={accent} />
    <line x1="8.9" y1="10.1" x2="13.4" y2="10.1" stroke={accent} />
    <line x1="8.9" y1="11.5" x2="13.4" y2="11.5" stroke={accent} />
  </Icon>
);

/** Clipboard + check — the procedure is covered */
export const ProcedureCoveredIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <rect x="5" y="4.2" width="14" height="16.6" rx="2.2" />
    <path d="M9 4.2V3.4A1.4 1.4 0 0 1 10.4 2h3.2A1.4 1.4 0 0 1 15 3.4v.8" />
    <polyline points="8.6 12.2 11 14.6 16 9.4" stroke={accent} strokeWidth="1.9" />
  </Icon>
);

/** Graft block descending into a bone defect — FlexiOss is utilized */
export const GraftUtilizedIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d="M4 13.5a8 8 0 0 0 16 0" />
    <rect x="9" y="3.2" width="6" height="6" rx="1.3" stroke={accent} />
    <line x1="12" y1="9.6" x2="12" y2="12.4" stroke={accent} />
    <polyline points="10 11 12 13.2 14 11" stroke={accent} />
  </Icon>
);

/** Peso coin + down arrow — out-of-pocket reduced */
export const CaseRateBenefitIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <circle cx="10" cy="12" r="7.3" />
    <path d="M8.2 9.3v5.4" stroke={accent} />
    <path d="M8.2 9.3h2.1a1.85 1.85 0 0 1 0 3.7H8.2" stroke={accent} />
    <line x1="7.1" y1="10.8" x2="11" y2="10.8" stroke={accent} />
    <line x1="20" y1="6" x2="20" y2="11" stroke={accent} />
    <polyline points="18 9 20 11 22 9" stroke={accent} />
  </Icon>
);

/** Bone + graft granules — standalone / adjunctive bone grafting */
export const BoneGraftIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d={BONE} transform="translate(1.5 0)" />
    <circle cx="15" cy="8.5" r="0.85" fill={accent} stroke={accent} />
    <circle cx="17.6" cy="11" r="0.85" fill={accent} stroke={accent} />
    <circle cx="15" cy="13.5" r="0.85" fill={accent} stroke={accent} />
    <circle cx="18" cy="15.6" r="0.85" fill={accent} stroke={accent} />
  </Icon>
);

/** Stacked vertebrae — spinal fusion */
export const SpineStackIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <rect x="7.5" y="3.4" width="9" height="3.8" rx="1.3" />
    <rect x="7.5" y="9.1" width="9" height="3.8" rx="1.3" />
    <rect x="7.5" y="14.8" width="9" height="3.8" rx="1.3" />
    <line x1="12" y1="7.2" x2="12" y2="9.1" stroke={accent} />
    <line x1="12" y1="12.9" x2="12" y2="14.8" stroke={accent} />
  </Icon>
);

/** Tooth — dental / maxillofacial */
export const ToothIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d="M6.8 4.4c1.5-1.1 3-1 4.6.1l.6.4.6-.4c1.6-1.1 3.1-1.2 4.6-.1 1.4 1 1.5 3.1.9 5.4-.5 2-1 4-1.5 6.4-.3 1.3-1.8 1.3-2.1 0L13 16c-.2-1-.6-1.5-1-1.5s-.8.5-1 1.5l-.5 3.2c-.3 1.3-1.8 1.3-2.1 0-.5-2.4-1-4.4-1.5-6.4-.6-2.3-.5-4.4.9-5.4z" />
    <path d="M9 7.6c.9-.6 1.8-.6 2.6 0" stroke={accent} />
  </Icon>
);

/** Bone with fracture line — non-union / revision */
export const NonUnionIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d={BONE} transform="translate(6 0)" />
    <polyline points="9.6 9 13 10.6 9.6 13.4 13 15" stroke={accent} strokeWidth="1.8" />
  </Icon>
);

/** Ear — otology / skull base */
export const EarIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d="M8 9.2a4 4 0 1 1 7.4 2.1c-.6 1-1.6 1.6-2.3 2.5-.6.8-.7 1.9-1.3 2.7a2.1 2.1 0 0 1-3.7-.8" />
    <path d="M10.6 9.4a1.7 1.7 0 0 1 2.6 1.4" stroke={accent} />
  </Icon>
);

/** Surgeon + coverage shield — for surgeons */
export const SurgeonCoverageIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <circle cx="9" cy="6.3" r="2.5" />
    <path d="M4.4 18.5v-1.4A4.2 4.2 0 0 1 8.6 12.9h.8a4.2 4.2 0 0 1 2.3.7" />
    <path d="M16.6 11.4l3.4 1.3v2.4c0 2.1-1.4 4-3.4 4.6-2-.6-3.4-2.5-3.4-4.6v-2.4l3.4-1.3z" stroke={accent} />
    <polyline points="15.3 15.4 16.3 16.4 18 14.4" stroke={accent} />
  </Icon>
);

/** Heart cradled by a hand — for patients (pass accent on gold surfaces) */
export const PatientCareIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d="M12 5.2c1.1-1 2.7-.9 3.7.2 1 1.1.9 2.7-.2 3.7L12 12.6 8.5 9.1c-1.1-1-1.2-2.6-.2-3.7 1-1.1 2.6-1.2 3.7-.2z" stroke={accent} />
    <path d="M6.5 14.8c0 2.4 2.5 4.2 5.5 4.2s5.5-1.8 5.5-4.2" />
    <path d="M6.5 14.8a2 2 0 0 0-2 2M17.5 14.8a2 2 0 0 1 2 2" />
  </Icon>
);

/** Calculator — interactive case-rate tool */
export const CalculatorIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <rect x="5.5" y="3" width="13" height="18" rx="2.2" />
    <rect x="8" y="5.4" width="8" height="3.2" rx="0.7" />
    <circle cx="9" cy="12" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="12" cy="12" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="15" cy="12" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="9" cy="15" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="12" cy="15" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="9" cy="18" r="0.7" fill="currentColor" stroke="none" />
    <circle cx="12" cy="18" r="0.7" fill="currentColor" stroke="none" />
    <rect x="14.3" y="14.4" width="1.4" height="4.4" rx="0.5" fill={accent} stroke={accent} />
  </Icon>
);

/** Speech bubble + ? — FAQ */
export const FaqIcon = ({ accent = GOLD, ...props }) => (
  <Icon {...props}>
    <path d="M4.5 5.5h15A1.5 1.5 0 0 1 21 7v7.5a1.5 1.5 0 0 1-1.5 1.5H10l-3.8 3.3V16H4.5A1.5 1.5 0 0 1 3 14.5V7a1.5 1.5 0 0 1 1.5-1.5z" />
    <path d="M10.2 9.6a1.9 1.9 0 0 1 3.6.9c0 1.3-1.8 1.6-1.8 2.9" stroke={accent} />
    <circle cx="12" cy="14.7" r="0.55" fill={accent} stroke={accent} />
  </Icon>
);
